import React from 'react';
import { StyledCategoryContainer, StyledContainer, StyledTitle, styles } from './ExplodedCategory.styles';
import BrowseCategoryBanner from './components/BrowseCategoryBanner';
import { SkeletonText } from '@shared/components/Skeleton';
import { CategoryRecommendationsCarousel } from '../CategoryRecommendationsCarousel/CategoryRecommendationsCarousel';
import { SpacingStack } from '@withjoy/joykit';
import { TopCategoryProductsCarousel } from './components/TopCategoryProductsCarousel/TopCategoryProductsCarousel';
import { pxToRem } from '@withjoy/joykit/theme';
import { GetRecommendedCatalogCategoriesQuery } from '@graphql/generated';
import { SubcategorySpotlight } from './components/SubcategorySpotlight';
import { BrowseMoreBanner } from './components/BrowseMoreBanner';

type CatalogCategoryData = Extract<
  GetRecommendedCatalogCategoriesQuery['recommendCatalogHomeSections']['sections'][number]['catalogEntities'][number],
  { __typename: 'ProductCatalogEntityCategory' }
>['catalogCategoryData'];
type SubCategories = Omit<Extract<CatalogCategoryData, { id: string }>['subCategories'][number], 'directItemCount'>[];
type BrowseBannerMore = Extract<CatalogCategoryData, { id: string }>['browseMoreBanner'];
export interface ExplodedCategory {
  id: string;
  title: string;
  publicUrlPath: string;
}

export interface ExplodedCategoryProps {
  id: string;
  title?: string;
  publicUrlPath?: string;
  browseMoreBanner?: BrowseBannerMore;
  subcategories: SubCategories;
  browseCategoryBannerText?: string;
  onBrowseCategoryClick: (category: ExplodedCategory, source: 'categoryRecShowAll' | 'categoryRecBrowseMore' | 'categoryShowcase', skipNavigation?: boolean) => void;
}

export const ExplodedCategory = ({ id, title, publicUrlPath, subcategories, onBrowseCategoryClick, browseCategoryBannerText, browseMoreBanner }: ExplodedCategoryProps) => {
  const showBrowseMoreBanner = browseMoreBanner?.image?.url && title && publicUrlPath;
  const showBrowseCategoryBanner = !showBrowseMoreBanner && title && browseCategoryBannerText && publicUrlPath;
  const subcategoriesCount = subcategories.length;
  return (
    <StyledContainer
      key={id}
      __css={styles.container}
    >
      {title && (
        <StyledTitle
          {...styles.title}
          typographyVariant={['HedMobile32', null, 'Hed56']}
        >
          {title}
        </StyledTitle>
      )}
      <SpacingStack spacing={pxToRem(98)}>
        {subcategories.map((subcategory, index) => {
          const zIndex = subcategoriesCount - index;
          const showSpotlight = (subcategory.spotlight?.image?.url || subcategory.spotlight?.videoUrl) && subcategory.spotlight?.title;
          if (showSpotlight) {
            return (
              <SubcategorySpotlight
                key={subcategory.id}
                zIndex={zIndex}
                title={subcategory.spotlight?.title || ''}
                subcategory={subcategory}
                onBrowseCategoryClick={onBrowseCategoryClick}
              />
            );
          }
          return (
            <TopCategoryProductsCarousel
              zIndex={[null, null, zIndex]}
              key={subcategory.id}
              category={subcategory}
              onBrowseCategoryClick={onBrowseCategoryClick}
            />
          );
        })}
        {showBrowseMoreBanner && (
          <BrowseMoreBanner
            categoryTitle={title}
            backgroundImageURL={browseMoreBanner?.image?.url || ''}
            onBrowseCategoryClick={() => onBrowseCategoryClick({ id, title, publicUrlPath }, 'categoryRecBrowseMore', false)}
          />
        )}
        {showBrowseCategoryBanner && (
          <BrowseCategoryBanner
            categoryTitle={browseCategoryBannerText}
            onBrowseCategoryClick={() => onBrowseCategoryClick({ id, title, publicUrlPath }, 'categoryRecBrowseMore', false)}
          />
        )}
      </SpacingStack>
    </StyledContainer>
  );
};

ExplodedCategory.Skeleton = ({ subcategoryCount, id }: { subcategoryCount: number; id: string }) => {
  return (
    <StyledContainer
      key={id}
      __css={styles.container}
    >
      <SkeletonText
        rows={1}
        skeletonHeight={56}
        width={[200, 500]}
        marginBottom={10}
        marginTop={10}
        alignSelf="center"
      />
      {Array.from({ length: subcategoryCount }).map((_, index) => (
        <StyledCategoryContainer
          key={index}
          __css={styles.categoryContainer}
        >
          <CategoryRecommendationsCarousel.Skeleton />
        </StyledCategoryContainer>
      ))}
      <BrowseCategoryBanner.Skeleton />
    </StyledContainer>
  );
};
